import "./index.css";
import { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

// Import route guards directly (not lazy loaded - small components)
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminProtectedRoute from "./components/auth/AdminProtectedRoute";
import { LanguageProvider } from "./i18n/LanguageContext";

// Lazy load all page components for code splitting
const LoginForm = lazy(() => import("./components/auth/LoginForm"));
const RegisterForm = lazy(() => import("./components/auth/RegisterForm"));
const ForgotPasswordPage = lazy(() => import("./components/auth/ForgotPasswordPage").then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("./components/auth/ResetPasswordPage").then(m => ({ default: m.ResetPasswordPage })));
const AcceptInvitePage = lazy(() => import("./components/auth/AcceptInvitePage").then(m => ({ default: m.AcceptInvitePage })));
const Dashboard = lazy(() => import("./components/dashboard/Dashboard"));
const FtpManagementPage = lazy(() => import("./components/admin/FtpManagementPage"));
const AdminPage = lazy(() => import("./components/admin/AdminPage"));
const BillingPage = lazy(() => import("./components/billing/BillingPage").then(m => ({ default: m.BillingPage })));
const FtpServerPage = lazy(() => import("./components/ftpserver/FtpServerPage").then(m => ({ default: m.FtpServerPage })));
const FtpUserPage = lazy(() => import("./components/ftpserver/FtpUserPage").then(m => ({ default: m.FtpUserPage })));
const FileBrowserPage = lazy(() => import("./components/ftpserver/FileBrowserPage").then(m => ({ default: m.FileBrowserPage })));
const FtpServerSettingsPage = lazy(() => import("./components/ftpserver/FtpServerSettingsPage").then(m => ({ default: m.FtpServerSettingsPage })));
const WebhookPage = lazy(() => import("./components/webhook/WebhookPage").then(m => ({ default: m.WebhookPage })));
const ApiEndpointPage = lazy(() => import("./components/apiendpoint/ApiEndpointPage").then(m => ({ default: m.ApiEndpointPage })));
const AuditLogPage = lazy(() => import("./components/auditlog/AuditLogPage").then(m => ({ default: m.AuditLogPage })));
const ProfilePage = lazy(() => import("./components/profile/ProfilePage").then(m => ({ default: m.ProfilePage })));
const UsersPage = lazy(() => import("./components/users/UsersPage").then(m => ({ default: m.UsersPage })));
const SettingsPage = lazy(() => import("./components/settings/SettingsPage").then(m => ({ default: m.SettingsPage })));
const AnalyticsDashboard = lazy(() => import("./components/analytics/AnalyticsDashboard"));
const ApiDocumentation = lazy(() => import("./components/docs/ApiDocumentation").then(m => ({ default: m.ApiDocumentation })));
const PrivacyPolicyPage = lazy(() => import("./components/legal/PrivacyPolicyPage").then(m => ({ default: m.PrivacyPolicyPage })));
const TermsOfServicePage = lazy(() => import("./components/legal/TermsOfServicePage").then(m => ({ default: m.TermsOfServicePage })));

// Loading component for Suspense fallback
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center">
      <div className="text-4xl font-black uppercase tracking-tighter mb-4 animate-pulse">
        Loading...
      </div>
      <div className="text-sm font-mono text-muted-foreground">
        Please wait
      </div>
    </div>
  </div>
);

export function App() {
  return (
    <LanguageProvider>
      <Router>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginForm />} />
          <Route path="/register" element={<RegisterForm />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/pricing" element={<BillingPage />} />

          {/* User Protected routes */}
          <Route path="/dashboard" element={<ProtectedRoute />}>
            <Route index element={<Dashboard />} />
          </Route>

          {/* Analytics routes - protected */}
          <Route path="/analytics" element={<ProtectedRoute />}>
            <Route index element={<AnalyticsDashboard />} />
          </Route>

          {/* Profile routes - protected */}
          <Route path="/profile" element={<ProtectedRoute />}>
            <Route index element={<ProfilePage />} />
          </Route>

          {/* Users routes - protected */}
          <Route path="/users" element={<ProtectedRoute />}>
            <Route index element={<UsersPage />} />
          </Route>

          {/* Settings routes - protected */}
          <Route path="/settings" element={<ProtectedRoute />}>
            <Route index element={<SettingsPage />} />
          </Route>

          {/* Billing routes - protected */}
          <Route path="/billing" element={<ProtectedRoute />}>
            <Route index element={<BillingPage />} />
          </Route>

          {/* FTP Server routes - protected */}
          <Route path="/ftp-servers" element={<ProtectedRoute />}>
            <Route index element={<FtpServerPage />} />
          </Route>

          <Route
            path="/ftp-servers/:ftpServerId/users"
            element={<ProtectedRoute />}
          >
            <Route index element={<FtpUserPage />} />
          </Route>

          <Route path="/ftp-servers/:id/files" element={<ProtectedRoute />}>
            <Route index element={<FileBrowserPage />} />
          </Route>

          <Route path="/ftp-servers/:id/settings" element={<ProtectedRoute />}>
            <Route index element={<FtpServerSettingsPage />} />
          </Route>

          {/* Webhook routes - protected */}
          <Route path="/webhooks" element={<ProtectedRoute />}>
            <Route index element={<WebhookPage />} />
          </Route>

          {/* API Endpoint routes - protected */}
          <Route path="/api-endpoints" element={<ProtectedRoute />}>
            <Route index element={<ApiEndpointPage />} />
          </Route>

          {/* Audit Log routes - protected */}
          <Route path="/audit-logs" element={<ProtectedRoute />}>
            <Route index element={<AuditLogPage />} />
          </Route>

          {/* API Documentation - public (but could be protected if needed) */}
          <Route path="/api-docs" element={<ApiDocumentation />} />

          {/* Legal pages - public */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />

          {/* Admin Protected routes */}
          <Route path="/admin" element={<AdminProtectedRoute />}>
            <Route index element={<AdminPage />} />
            <Route path="ftp-management" element={<FtpManagementPage />} />
          </Route>

          {/* Redirect root to login or dashboard based on auth */}
          <Route
            path="/"
            element={
              localStorage.getItem("authToken") ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* Fallback for unknown routes (optional) */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </LanguageProvider>
  );
}

export default App;
